"use client";

import { useSyncExternalStore } from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useLanguage } from "@/contexts/LanguageContext";

const STORAGE_KEY = "petromin_cookie_consent_v1";

/**
 * `localStorage` lives outside React, so subscribe via `useSyncExternalStore`
 * to avoid the cascading-render anti-pattern of `setState` inside `useEffect`.
 * The server snapshot returns `true` (accepted) so the banner never renders
 * in SSR markup — it appears post-hydration only for users who haven't accepted.
 */
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function getServerSnapshot() {
  return true;
}

export function CookieConsentBanner() {
  const { t } = useLanguage();
  const accepted = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore quota / private mode */
    }
    listeners.forEach((cb) => cb());
  };

  if (accepted) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-white/10 bg-[#130F0E] text-white shadow-[0_-8px_32px_rgba(0,0,0,0.2)]"
      role="dialog"
      aria-modal="false"
      aria-label={t("cookiesBanner.ariaLabel")}
      aria-live="polite"
      id="hide-in-mobile-app"
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <p className="min-w-0 text-sm leading-relaxed text-white/85 wrap-break-word sm:max-w-[min(100%,42rem)]">
          {t("cookiesBanner.message")}{" "}
          <LocaleLink
            href="/cookie-policy"
            className="font-semibold text-[#BD2226] underline-offset-2 hover:underline"
          >
            {t("cookiesBanner.cookiePolicy")}
          </LocaleLink>
          {" · "}
          <LocaleLink
            href="/privacy-policy"
            className="font-semibold text-white underline-offset-2 hover:underline"
          >
            {t("cookiesBanner.privacyPolicy")}
          </LocaleLink>
        </p>
        <div className="flex shrink-0 items-center gap-2 sm:justify-end">
          <button
            type="button"
            onClick={accept}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-[#BD2226] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#C00322] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#130F0E] sm:w-auto sm:min-w-28"
          >
            {t("cookiesBanner.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
